import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditPayment,
  setEditPayment,
  toggleCreateModal,
} from "../../features/payments/paymentsSlice";
import { IFormInputNote, OptionsProps } from "../../types/payments";
import { useParams } from "react-router-dom";
import { t } from "../../locales";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Loader2 } from "lucide-react";

const partyModalSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: "48px",
    borderRadius: "0.75rem",
    borderWidth: "2px",
    borderColor: state.isFocused ? "#113F67" : "#e5e7eb",
    boxShadow: state.isFocused
      ? "0 0 0 2px #fff, 0 0 0 4px #113F67"
      : "none",
    paddingLeft: "0.5rem",
    fontSize: "0.875rem",
    "&:hover": {
      borderColor: state.isFocused ? "#113F67" : "#e5e7eb",
    },
  }),
  placeholder: (base: any) => ({ ...base, color: "#9ca3af", fontSize: "0.875rem" }),
  singleValue: (base: any) => ({ ...base, color: "#11204d", fontSize: "0.875rem" }),
  input: (base: any) => ({ ...base, fontSize: "0.875rem" }),
  option: (base: any, { isSelected }: any) => ({
    ...base,
    color: "#11204d",
    backgroundColor: isSelected ? "#e5e7eb" : base.backgroundColor,
    fontSize: "0.875rem",
  }),
  menu: (base: any) => ({ ...base, zIndex: 60 }),
} as any;

type Props = {
  handleSavePayment: (data: IFormInputNote) => void;
};

const NoteForm = ({ handleSavePayment }: Props) => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const editPayment = useAppSelector(getEditPayment);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const noteTypeOption: OptionsProps[] = [
    { value: 1, label: t.payments.adjustmentTypes.extra },
    { value: 2, label: t.payments.adjustmentTypes.roundOff },
    { value: 3, label: t.payments.adjustmentTypes.taxAmount },
    { value: 4, label: t.payments.adjustmentTypes.rejectedQuality },
    { value: 5, label: t.payments.adjustmentTypes.rejectedFewerPieces },
    { value: 6, label: t.payments.adjustmentTypes.lb },
  ];

  const noteStatusOption: OptionsProps[] = [
    { value: 0, label: t.payments.adjustmentStatus.addAmount },
    { value: 1, label: t.payments.adjustmentStatus.deductAmount },
  ];

  const {
    handleSubmit,
    register,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<IFormInputNote>({
    defaultValues: { amount: null, note: null, status: null },
  });

  const handleOnSubmit: SubmitHandler<IFormInputNote> = async (data: any) => {
    setIsLoading(true);
    const note = noteTypeOption?.find((n) => n.value === data.note)?.label || null;

    if (editPayment && editPayment.id) {
      await handleSavePayment({ ...data, id: editPayment.id, type: 2, note });
    } else {
      await handleSavePayment({ ...data, type: 2, party_id: id, note });
    }
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleCreateModal(0));
    dispatch(setEditPayment(null));
  };

  useEffect(() => {
    if (editPayment && editPayment.id) {
      const note = noteTypeOption?.find((n) => n.label === editPayment.note)?.value || null;
      reset({ amount: editPayment.amount, note, status: editPayment.type });
    } else {
      reset({ amount: null, note: null, status: null });
    }
  }, [editPayment]);

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) handleCloseFormModal(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#113F67]">{t.payments.forms.adjustmentTitle}</DialogTitle>
          <DialogDescription>{t.payments.actions.adjustment}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-4 flex-1 flex flex-col sm:block pb-16 sm:pb-0 sm:space-y-5">
          <div className="space-y-2">
            <Label>{t.payments.formFields.note}</Label>
            <Controller
              name="note"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <ReactSelect
                  styles={partyModalSelectStyles}
                  placeholder={t.payments.formFields.note}
                  options={noteTypeOption as any}
                  value={
                    field.value !== null && field.value !== undefined
                      ? noteTypeOption.find((o) => o.value === field.value) || null
                      : null
                  }
                  onChange={(selected: any) => setValue("note", selected ? selected.value : null)}
                />
              )}
            />
            {errors.note && <span className="text-red-500 text-sm">{t.payments.formFields.note}</span>}
          </div>

          <div className="space-y-2">
            <Label>{t.payments.formFields.status}</Label>
            <Controller
              name="status"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <ReactSelect
                  styles={partyModalSelectStyles}
                  placeholder={t.payments.formFields.status}
                  options={noteStatusOption as any}
                  value={
                    field.value !== null && field.value !== undefined
                      ? noteStatusOption.find((o) => o.value === field.value) || null
                      : null
                  }
                  onChange={(selected: any) => setValue("status", selected ? selected.value : null)}
                />
              )}
            />
            {errors.status && <span className="text-red-500 text-sm">{t.payments.formFields.status}</span>}
          </div>

          <div className="space-y-2">
            <Label>{t.payments.formFields.amount}</Label>
            <Input
              type="number"
              step="any"
              placeholder={t.payments.formFields.amount}
              {...register("amount", { required: true, min: 0 })}
              className="h-12"
              onFocus={(e) => e.target.addEventListener("wheel", (e) => e.preventDefault(), { passive: false })}
            />
            {errors.amount && <span className="text-red-500 text-sm">{t.payments.formFields.amount}</span>}
          </div>

          <div className="fixed bottom-0 left-0 right-0 flex gap-3 p-4 justify-end border-t bg-white z-10 sm:static sm:p-0 sm:pt-2 sm:z-auto sm:border-t sm:bg-transparent">
            <Button
              type="button"
              variant="outline"
              className="h-11 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              {t.payments.buttons.close}
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="h-11 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
            >
              {isLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />{t.payments.buttons.saving}</> : t.payments.buttons.continue}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NoteForm;
